/**
 * runMotion — общие утилиты анимации run-канвы (редизайн 0.14.1).
 *
 * prefers-reduced-motion читается из ДВУХ каналов (как CSS-слой проекта):
 *  1. системная настройка ОС — matchMedia('(prefers-reduced-motion: reduce)')
 *     (нативный baseline, слой 0 из a11ySettings);
 *  2. гранулярная in-app панель — data-a11y-motion="reduce" на <html>
 *     (слой 1; см. a11ySettings.ts).
 *
 * JS-канал нужен там, где анимация управляется кодом (кадры ходьбы по
 * data-frame, встряска канвы тоглом класса), а не только CSS: при reduce
 * остаются мгновенные состояния (пятно появляется сразу, спрайт стоит).
 * Безопасно в jsdom: отсутствующий matchMedia — false.
 *
 * 0.14.3 (M1): useTilePx — px-размер плитки для относительных единиц
 * (CSS-переменная --tile, механизм --ex из 0.14.1, обобщённый на канву).
 */
import { useEffect, useState, type RefObject } from 'react';

/** true — пользователь просил уменьшить движение (шейк/частицы/винты off). */
export function prefersReducedMotion(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.documentElement.getAttribute('data-a11y-motion') === 'reduce') return true;
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/** Fallback-размер плитки, пока layout не измерен (первый кадр) и в jsdom
 *  (clientWidth всегда 0) — те же 500px, что исторический pxRef/--ex. */
export const RUN_TILE_FALLBACK_PX = 500;

/**
 * Ширина run-плитки в px (M1, 0.14.3): источник относительных размеров канвы
 * (CSS-переменная --tile на корне компонента; спрайты/подписи/счётчики —
 * доли плитки через clamp(), см. блок run в index.css). Механизм — образец
 * --ex из 0.14.1 (px на плитку из clientWidth), обобщённый: resize-обновление
 * через ResizeObserver (jsdom без RO — одноразовый замер + fallback 500).
 */
export function useTilePx(ref: RefObject<HTMLElement | null>): number {
  const [tile, setTile] = useState(RUN_TILE_FALLBACK_PX);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): void => {
      const w = el.clientWidth;
      setTile((prev) => (w > 0 && w !== prev ? w : prev));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return; // jsdom — фикс 500.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return tile;
}
